import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Loader2, BarChart3 } from "lucide-react";

export default function PublisherSectors() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/publisher/sectors?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [] }));
  }, [days]);

  const totalSent = (data?.rows || []).reduce((s, r) => s + r.sent, 0);

  return (
    <div className="space-y-6" data-testid="publisher-sectors-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Sectors</h1>
          <p className="text-[13px] text-slate-500 mt-1">Performance breakdown by sector — see which verticals you produce best.</p>
        </div>
        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 text-[11px]">
          {[7, 30, 90, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full transition-colors ${days === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`sectors-period-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!data && <div className="text-center py-12 text-slate-500"><Loader2 className="size-4 animate-spin inline mr-1" />Loading…</div>}

      {data && data.rows.length === 0 && (
        <Card className="rounded-xl border-slate-200">
          <CardContent className="p-12 text-center text-slate-500 text-[13px]">
            <BarChart3 className="size-6 mx-auto mb-2 text-slate-300" />
            No leads in this window yet.
          </CardContent>
        </Card>
      )}

      {data && data.rows.length > 0 && (
        <>
          {/* Top KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Total sent" value={totalSent.toLocaleString()} sub={`${data.rows.length} active sectors`} />
            <KpiTile label="Total accepted" value={data.rows.reduce((s, r) => s + r.accepted, 0).toLocaleString()} sub={`${((data.rows.reduce((s, r) => s + r.accepted, 0) / Math.max(1, totalSent)) * 100).toFixed(1)}% accept rate`} />
            <KpiTile label="Total returned" value={data.rows.reduce((s, r) => s + r.returned, 0).toLocaleString()} sub="Buyer-flagged quality returns" />
            <KpiTile label="Total earnings" value={`£${data.rows.reduce((s, r) => s + r.earnings_gbp, 0).toFixed(2)}`} sub="After buyer acceptance" highlight />
          </div>

          {/* Per-sector table */}
          <Card className="rounded-xl border-slate-200 overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sectors-table">
                  <thead className="bg-slate-50/60 border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="px-5 py-3 font-medium">Sector</th>
                      <th className="font-medium text-right">Sent</th>
                      <th className="font-medium text-right">Accepted</th>
                      <th className="font-medium text-right">Returned</th>
                      <th className="font-medium text-right">Sold</th>
                      <th className="font-medium text-right">Accept rate</th>
                      <th className="font-medium text-right pr-5">Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.sector} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`sector-row-${r.sector}`}>
                        <td className="px-5 py-3.5 text-[13px] text-slate-900 capitalize">{r.sector.replace(/-/g, " ")}</td>
                        <td className="py-3.5 text-right tabular-nums text-slate-700">{r.sent}</td>
                        <td className="py-3.5 text-right tabular-nums text-slate-700">{r.accepted}</td>
                        <td className="py-3.5 text-right tabular-nums text-rose-700">{r.returned}</td>
                        <td className="py-3.5 text-right tabular-nums text-emerald-700 font-medium">{r.sold}</td>
                        <td className="py-3.5 text-right tabular-nums">
                          <span className={`inline-block text-[11.5px] px-2 py-0.5 rounded-full ${r.accept_rate >= 0.8 ? "bg-emerald-50 text-emerald-700" : r.accept_rate >= 0.5 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
                            {(r.accept_rate * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3.5 pr-5 text-right tabular-nums text-slate-900 font-medium">£{r.earnings_gbp.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, highlight }) {
  return (
    <Card className={`rounded-xl border-slate-200 ${highlight ? "bg-[#0055FF]/[0.025]" : ""}`}>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-semibold tabular-nums ${highlight ? "text-[#0055FF]" : "text-slate-900"}`}>{value}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}
